import { Badge } from '@/components/Atoms/Badge/Badge';
import { BadgeList } from '@/components/Atoms/BadgeList/BadgeList';
import { MarkedText } from '@/components/Atoms/MarkedText/MarkedText';
import { MediaCardImage } from '@/components/Atoms/MediaCardImage/MediaCardImage';
import { TimeAgo } from '@/components/Atoms/TimeAgo/TimeAgo';
import {
    Card,
    CardActions,
    CardBody,
    CardDescription,
    CardImage,
    CardMetadata,
} from '@/components/Molecules/Card/Card';
import { ContentPin } from '@/components/Molecules/ContentPin/ContentPin';
import { ContentStatus } from '@/components/Molecules/ContentStatus/ContentStatus';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { SubscribeButton } from '@/components/Molecules/SubscribeButton/SubscribeButton';
import { CountryCard } from '@/components/Organisms/CountryCard/CountryCard';
import { FlagshipCard } from '@/components/Organisms/FlagshipCard/FlagshipCard';
import { SectorCard } from '@/components/Organisms/SectorCard/SectorCard';
import { SeriesCard } from '@/components/Organisms/SeriesCard/SeriesCard';
import { ToolCard } from '@/components/Organisms/ToolCard/ToolCard';
import useTranslation from '@/hooks/useTranslation';
import type { IAnalyticsTraceableElement, IContentItem, IPinableItem, ISectorPage, ISeriesItem } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { ResponsiveImageHints } from '@/services/ResponsiveImageHints';
import classNames from '@/utils/classNames';
import { buildFromGivenWithSearch } from '@/utils/siteUtils';
import ClockIcon from '@bloomberg/web-icons-react/Clock';
import PaperclipIcon from '@bloomberg/web-icons-react/Paperclip';
import type { Country, Tool } from 'bnef';
import type React from 'react';
import type { ComponentProps } from 'react';
import { useId } from 'react';
import type { FCl } from 'react-fc';
import styles from './ContentItemCard.module.css';

export interface IContentItemCardProps extends IAnalyticsTraceableElement {
    item: IContentItem & IPinableItem;
    pinned?: boolean;
    contextQuery?: string;
    featured?: boolean;
    imageBorder?: ComponentProps<typeof CardImage>['border'];
    imageRatio?: number;
    withImage?: ComponentProps<typeof Card>['withImage'];
    withSummary?: boolean;
    withSaveButton?: boolean;
    withSeries?: boolean;
    withDates?: boolean;
    withSavedSearches?: boolean;
    seriesCardProps?: Omit<ComponentProps<typeof SeriesCard>, 'series' | 'pinned'>;
    sectorCardProps?: Omit<ComponentProps<typeof SectorCard>, 'sector' | 'pinned'>;
    toolsCardProps?: Omit<ComponentProps<typeof ToolCard>, 'tool' | 'pinned'>;
    responsive?: ComponentProps<typeof Card>['responsive'];
    headingLevel?: keyof React.JSX.IntrinsicElements;
    boxed?: boolean;
    extraActions?: (item: IContentItem & IPinableItem) => React.ReactNode;
    useFlagshipCard?: boolean;
}

/**
 * A responsive card for a content item with an image, type, title and actions.
 * Will automatically show the image horizontal or vertical depending on available space
 */
export const ContentItemCard: FCl<IContentItemCardProps> = ({
    analyticsBaseId,
    analyticsCategory = TrackingCategory.BnefContent,
    className,
    item,
    position,
    contextQuery,
    imageBorder,
    featured = false,
    pinned = false,
    imageRatio = 16 / 9,
    withImage = true,
    withSummary = false,
    withSaveButton = true,
    withSeries = true,
    withDates = true,
    withSavedSearches = true,
    seriesCardProps,
    sectorCardProps,
    toolsCardProps,
    responsive = true,
    headingLevel = 'h4',
    boxed = false,
    extraActions,
    useFlagshipCard = true,
}) => {
    const { t } = useTranslation();
    const titleId = useId();
    if (item.type === ContentType.flagship && useFlagshipCard) {
        return <FlagshipCard flagship={item as unknown as IContentItem} />;
    }
    if (item.type === ContentType.countryprofile) {
        return <CountryCard country={item as unknown as Country} pinned={pinned} />;
    }
    if (item.type === ContentType.sector) {
        return <SectorCard sector={item as unknown as ISectorPage} pinned={pinned} {...sectorCardProps} />;
    }
    if (item.type === ContentType.series) {
        return <SeriesCard series={item as unknown as ISeriesItem} pinned={pinned} {...seriesCardProps} />;
    }
    if (item.type === ContentType.tool) {
        return (
            <ToolCard
                tool={item as unknown as Tool}
                pinned={pinned}
                showToolSavedSearches={withSavedSearches}
                allowSave={withSaveButton}
                showDescription={withSummary}
                {...toolsCardProps}
            />
        );
    }

    const showPlayButton = Boolean(item.type === 'video' || item.videoDuration);
    const saveId = item.id;
    const saveType: string = item.type;

    const attachmentValues = new Array<string>();
    if (item.flags?.includes('hasData')) {
        attachmentValues.push('Excel');
    }
    if (item.flags?.includes('hasSlides')) {
        attachmentValues.push('PowerPoint');
    }

    const attachmentString = attachmentValues.join(', ');

    let url = item.url;
    if (contextQuery) {
        url = buildFromGivenWithSearch(item.url, new Map([['context', contextQuery]]));
    }

    const showSummary = Boolean((featured || withSummary) && item.summary);

    const customActions = extraActions?.(item);

    return (
        <Card
            id={titleId}
            title={<MarkedText value={item.title} className={classNames({ 'cds-t-h2': featured })} />}
            href={url}
            headingLevel={headingLevel}
            responsive={responsive}
            className={className}
            withImage={withImage}
            boxed={boxed}
            data-analytics-tracking={buildTrackingObject(
                TrackingAction.FollowLink,
                analyticsCategory,
                analyticsBaseId,
                position
            )}
            prefix={
                <Stack>
                    <BadgeList>
                        {withSeries && item.parentSeries && (
                            <Badge text={t('ContentType.series')} link="/series" colorVar="blue" />
                        )}
                        <ContentTypeHeader type={item.type} eventSubtype={item.eventSubtype} />
                        {(item.pinned || pinned) && <ContentPin />}
                        <ContentStatus status={item.contentStatus} />
                    </BadgeList>
                    {withSeries && item.parentSeries && (
                        <p>
                            <a href={item.parentSeries.url}>
                                <MarkedText value={item.parentSeries.title} />
                            </a>
                        </p>
                    )}
                </Stack>
            }
        >
            {withImage && item.leadImage && (
                <CardImage border={imageBorder}>
                    <MediaCardImage
                        imageHints={
                            new ResponsiveImageHints({ initial: '100vw', atM: '50vw', atXL: '25vw' }, null, imageRatio)
                        }
                        url={item.leadImage}
                        showPlayButton={showPlayButton}
                        durationSeconds={item.videoDuration}
                    />
                </CardImage>
            )}
            {(showSummary || (item.displayDate && withDates)) && (
                <CardBody>
                    {showSummary && (
                        <CardDescription className={classNames({ [styles.responsiveDescription]: responsive })}>
                            <MarkedText value={item.summary} />
                        </CardDescription>
                    )}
                    <CardMetadata
                        items={[
                            item.displayDate
                                ? {
                                      icon: <ClockIcon />,
                                      label: t('common:Labels.published'),
                                      value: <TimeAgo datetime={item.displayDate} />,
                                  }
                                : undefined,
                            attachmentValues.length > 0
                                ? {
                                      icon: <PaperclipIcon />,
                                      label: t('common:SearchFilter.filterNames.attachment'),
                                      value: attachmentString,
                                  }
                                : undefined,
                        ]}
                    />
                </CardBody>
            )}

            {(withSaveButton || withSeries || customActions) && (
                <CardActions>
                    {withSeries && item.parentSeries && (
                        <SubscribeButton series={item.parentSeries} analyticsBaseId={analyticsBaseId} />
                    )}
                    {withSaveButton && (
                        <SaveButton
                            aria-describedby={titleId}
                            id={saveId}
                            type={saveType}
                            itemName={item.title}
                            analyticsBaseId={analyticsBaseId}
                        />
                    )}
                    {customActions}
                </CardActions>
            )}
        </Card>
    );
};
